import { ChangeEvent, ReactNode, SyntheticEvent, useEffect, useMemo, useState } from 'react'
import { Box, IconButton, SelectChangeEvent, Skeleton, Typography } from '@mui/material'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '@frontegg/react'
import { hasAnyRole, Role } from 'lib/utils'
import debounce from 'lodash.debounce'
import { useQuery } from 'react-query'
import theme from '../../../../theme'

/* Utils */
import { useAppDispatch } from 'store/hooks'
import {
  CPILibraryConfigurationIntegration,
  CPILibraryItem,
  IntegrationInstance,
  IntegrationsFilters,
  IntegrationsSort,
  ManifestIntegration
} from 'models'
import {
  DATA_SOURCES_PAGE_TITLE,
  defaultIntegrationsCategories,
  DIRECTORY_TAB_LABEL,
  INTEGRATIONS_PAGE_TITLE,
  integrationsInstancesOptions,
  integrationsSortOptionsInstances,
  integrationsSortOptionsIntegrations
} from './integrations.constants'
import axios from 'lib/axios'
import { ModalType, openModal } from 'store/modals/modalSlice'
import { instanceOfManifestIntegration, isIntegrationNew } from './utils'

/* Components */
import IntegrationCard from './components/IntegrationCard'
import {
  AppContainer,
  ControlsSection,
  FiltersContainer,
  GridContainer,
  Header,
  IntegrationsGridWrapper,
  MainContent,
  ResetButton,
  ScrollableContent,
  StyledTab,
  StyledTabs
} from './integrations.styles'
import { IntegrationsSearch, IntegrationsSelectCustom, IntegrationsSelectField } from './form-components'
import SortButtonSelect from 'ui/components/form-components/SortButtonSelect'
import IntegrationInstanceDrawer from './IntegrationInstanceDrawer'
import { CpisListWrapper } from './addInstanceDrawer.styles'
import ChevronRight from '../../../components/svg/ChevronRight'
import AssetAnalyzer from '../../../components/svg/AssetAnalyzer'
import EmptyState from '../../coverage-analyzer/components/EmptyState'
import { SSMCardSkeleton, SSMCardSkeletonWrapper } from '../../security-stack-map/components/ssmCard.styles'
import { HotToast } from '../../../components/toast'
import toast from 'react-hot-toast'

const sortAlphabetically = (arr: any, sortBy: any) => {
  if (sortBy === IntegrationsSort.AZ) {
    return arr.sort((a: any, b: any) => {
      const nameA = a.name || a.integration_name || ''
      const nameB = b.name || b.integration_name || ''

      const displayA = nameA.replace(/_/g, ' ').toLowerCase()
      const displayB = nameB.replace(/_/g, ' ').toLowerCase()

      return displayA.localeCompare(displayB)
    })
  } else if (sortBy === IntegrationsSort.ZA) {
    return arr.sort((a: any, b: any) => {
      const nameA = a.name || a.integration_name || ''
      const nameB = b.name || b.integration_name || ''

      const displayA = nameA.replace(/_/g, ' ').toLowerCase()
      const displayB = nameB.replace(/_/g, ' ').toLowerCase()

      return displayB.localeCompare(displayA)
    })
  } else return arr
}

const isTicketingSystemsEnabled = process.env.REACT_APP_ENABLE_TICKETING_SYSTEMS === 'true'

const Integrations = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const { user: authUser } = useAuth()
  const [initialLoad, setInitialLoad] = useState(true)
  const [supportedIntegrations, setSupportedIntegrations] = useState<ManifestIntegration[]>([])
  const [supportedIntegrationsNew, setSupportedIntegrationsNew] = useState<ManifestIntegration[]>([])
  const [configuredIntegrations, setConfiguredIntegrations] = useState<IntegrationInstance[]>([])
  const [totalInstancesCount, setTotalInstancesCount] = useState<number>(0)
  const [totalIntegrationsCount, setTotalIntegrationsCount] = useState<number>(0)
  const [instances, setInstances] = useState<IntegrationInstance[]>([])
  const [tabValue, setTabValue] = useState(0)
  const [searchValue, setSearchValue] = useState('')
  const [instanceFilter, setInstanceFilter] = useState<string[]>([''])
  const [sortBy, setSortBy] = useState<IntegrationsSort>(integrationsSortOptionsIntegrations[0])
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [affectedCpis, setAffectedCpis] = useState<CPILibraryItem[][]>([])
  const [affectedCpisLoading, setAffectedCpisLoading] = useState(true)
  const [cpis, setCpis] = useState<CPILibraryItem[]>([])
  const [categoryOptions, setCategoryOptions] = useState<string[]>(defaultIntegrationsCategories)
  const [loading, setLoading] = useState(false)
  const [pageLoading, setPageLoading] = useState(true)
  const [jobId, setJobId] = useState('')
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [selectedIntegration, setSelectedIntegration] = useState<ManifestIntegration | null>(null)
  const [selectedInstance, setSelectedInstance] = useState<IntegrationInstance | null>(null)
  const [integrationType, setIntegrationType] = useState<'add' | 'edit'>('add')
  const [vendorOptions, setVendorOptions] = useState<string[]>([])
  const [selectedVendors, setSelectedVendors] = useState<any[]>([])
  const [filters, setFilters] = useState<IntegrationsFilters>({
    category: [],
    vendor: [],
    configurationStatus: [],
    product: []
  })
  const [productOptions, setProductOptions] = useState<any[]>([])
  const key = 'key1'

  const { data: asyncData } = useQuery(['jobId', { jobId }], () => axios.get(`/api/v3/async/result/${jobId}`), {
    refetchInterval: 5000,
    enabled: !!jobId
  })

  useEffect(() => {
    if (location.state && (location as any).state.jobId) {
      setJobId((location as any).state.jobId)
    }
  }, [location])

  useEffect(() => {
    if (asyncData && (location as any).state.integrationName && (location as any).state.cpis) {
      if (asyncData.status === 200) {
        if (asyncData.data.status === 'COMPLETED') {
          setJobId('')
          navigate(location.pathname, {})
          toast.success(() => (
            <HotToast
              text={`${(location as any).state.integrationName} was successfully added to ${
                (location as any).state.cpis
              } CPIs.`}
            />
          ))
        }
      }
    }
  }, [asyncData, location])

  // Filter "New" section to only show items present in current supported integrations results
  const filteredNewIntegrations = useMemo(() => {
    const currentNames = new Set(supportedIntegrations.map((n) => n.name))
    return supportedIntegrationsNew.filter((i) => currentNames.has(i.name))
  }, [supportedIntegrationsNew, supportedIntegrations])

  // "All Integrations" section excludes items already shown in "New" section
  const supportedIntegrationsAll = useMemo(() => {
    if (filteredNewIntegrations.length === 0) return supportedIntegrations
    const newNames = new Set(filteredNewIntegrations.map((n) => n.name))
    return supportedIntegrations.filter((i) => !newNames.has(i.name))
  }, [supportedIntegrations, filteredNewIntegrations])

  const fetchCpis = async () => {
    try {
      const res = await axios.get('/api/v3/cpis')

      if (res.status === 200) {
        const {
          data: {
            data: { cpis }
          }
        } = res
        setCpis(cpis)

        return cpis
      }
    } catch (err) {
      console.error(err)
    }

    return []
  }

  const fetchSupported = async () => {
    try {
      const res = await axios.get(
        // '/api/v3/integrations/supported?sort=desc&loadStatus=true'
        '/api/v3/integrations/supported?loadStatus=true'
      )

      if (res.status === 200) {
        return res.data.integrations
      }
    } catch (err) {
      console.error(err)
    }

    return []
  }

  const fetchConfigured = async () => {
    try {
      const sortByFormatted =
        sortBy === IntegrationsSort.NewestFirst || sortBy === IntegrationsSort.LatestCreated
          ? 'DESC'
          : sortBy === IntegrationsSort.OldestFirst || sortBy === IntegrationsSort.OldestCreated
          ? 'ASC'
          : ''
      let url = '/api/v3/integrations/configured?loadStatus=true'
      if (sortByFormatted) {
        url = `${url}&sort=${sortByFormatted}`
      }
      if (searchValue) {
        url = `${url}&search=${searchValue}`
      }
      if (filters.category.length > 0) {
        url = `${url}&categories=[${filters.category.map((n) => `"${n}"`)}]`
      }

      const res = await axios.get<{ instances: IntegrationInstance[] }>(url)

      if (res.status === 200) {
        const result = sortAlphabetically(res.data.instances, sortBy)

        return result as IntegrationInstance[]
      }
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }

    return []
  }

  const fetchInstances = async () => {
    setLoading(true)
    setPageLoading(true)

    try {
      const acaSupportedIntegrationNames: string[] = []

      const supported = await fetchSupported()
      const supportedNew: ManifestIntegration[] = []
      const supportedOld: ManifestIntegration[] = []
      const integrationCategories: string[] = []
      const vendorOptions: string[] = []
      const allVendors: any = []

      supported.forEach((integration: ManifestIntegration) => {
        const vendorDisplayName = integration.vendor.display_name
        if (!vendorOptions.includes(vendorDisplayName)) {
          vendorOptions.push(vendorDisplayName)
          allVendors.push(integration.vendor)
        }
        const integrationsCategories = integration.categories
        const isNew = isIntegrationNew(integration)
        if (isNew) {
          supportedNew.push(integration)
        } else {
          supportedOld.push(integration)
        }

        if (integrationsCategories && integrationsCategories.length > 0) {
          integrationsCategories.forEach((integrationsCategory: string) => {
            if (!integrationCategories.includes(integrationsCategory)) {
              integrationCategories.push(integrationsCategory)
            }
          })
        }
      })
      setCategoryOptions(integrationCategories.sort((a, b) => (a > b ? 1 : -1)))
      setSupportedIntegrations(supported.sort((a: any, b: any) => (a.display_name > b.display_name ? 1 : -1)))
      setSupportedIntegrationsNew(supportedNew.sort((a: any, b: any) => (a.display_name > b.display_name ? 1 : -1)))
      setTotalIntegrationsCount(supported.length + supportedNew.length)

      const filteredIntegrations: ManifestIntegration[] = supported.filter(
        (n: ManifestIntegration) => n.is_supports_aca
      )
      filteredIntegrations.forEach((integration) => acaSupportedIntegrationNames.push(integration.name))

      const configured = await fetchConfigured()
      const configuredProducts: string[] = []
      const configuredWithVendor = configured
        .map((c) => {
          if (!configuredProducts.includes(c.integration_name)) {
            configuredProducts.push(c.integration_name)
          }
          const found = supported.find((n: any) => n.name === c.integration_name)
          return {
            ...c,
            vendor: found?.vendor || null
          }
        })
        .sort((a: any, b: any) => (a.name > b.name ? 1 : -1))

      configuredWithVendor.sort((a: any, b: any) => {
        const nameA = a.name || a.integration_name || ''
        const nameB = b.name || b.integration_name || ''

        const displayA = nameA.replace(/_/g, ' ').toLowerCase()
        const displayB = nameB.replace(/_/g, ' ').toLowerCase()

        return displayA.localeCompare(displayB)
      })

      setConfiguredIntegrations(configuredWithVendor)
      setTotalInstancesCount(configuredWithVendor.length)
      let acaInstances: IntegrationInstance[] = configured
      if (acaSupportedIntegrationNames.length > 0) {
        acaInstances = acaInstances.filter((n) => acaSupportedIntegrationNames.includes(n.integration_name))
      }

      setInstances(acaInstances)

      if (initialLoad) {
        const products: any[] = configuredProducts.map((c) => c.replaceAll('_', ' '))
        setProductOptions(products.sort((a: any, b: any) => (a > b ? 1 : -1)))
        setVendorOptions(vendorOptions.sort((a, b) => (a > b ? 1 : -1)))
      }

      const state = location.state
      if (state && (state as any).instanceId) {
        const instanceId = (state as any).instanceId
        if (instanceId) {
          const instance = configured.find((n) => n.id === instanceId)
          if (instance) {
            setTabValue(1)
            setSelectedInstance(instance)
            setDetailsOpen(true)
            setTimeout(() => {
              navigate(location.pathname, {})
            }, 0)
          }
        }
      }
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
      setPageLoading(false)
    }
  }

  useEffect(() => {
    if (initialLoad) {
      fetchInstances().finally(() => setInitialLoad(false))
    }
  }, [initialLoad])

  const handleSearch = async (event: SyntheticEvent, value: string) => {
    const sortByFormatted =
      tabValue === 0
        ? sortBy === IntegrationsSort.NewestFirst
          ? 'DESC'
          : sortBy === IntegrationsSort.OldestFirst
          ? 'ASC'
          : ''
        : sortBy === IntegrationsSort.LatestCreated
        ? 'DESC'
        : sortBy === IntegrationsSort.OldestCreated
        ? 'ASC'
        : ''
    const categories = filters.category.filter((c) => c !== '')

    let baseUrl = `/api/v3/integrations/supported?search=${value}`
    // &categories=[${categories.map(
    //   (n) => `"${n}"`
    // )}]`
    if (tabValue === 1) {
      baseUrl = `/api/v3/integrations/configured?search=${value}&sort=${sortByFormatted}&categories=[${categories.map(
        (n) => `"${n}"`
      )}]`
    }

    const res = await axios.get(baseUrl)

    if (res.status === 200) {
      const { integrations, instances } = res.data

      if (tabValue === 0) {
        const vendorFiltered =
          filters.vendor.length > 0
            ? integrations.filter((c: any) => (filters.vendor as any).includes(c.vendor.display_name))
            : integrations

        let configStatusFiltered: any[] = [...vendorFiltered]

        if (filters.configurationStatus.length > 0) {
          if (filters.configurationStatus.length === 2) {
            configStatusFiltered = vendorFiltered
          } else if (
            filters.configurationStatus.length === 1 &&
            filters.configurationStatus.includes('Configured Instances')
          ) {
            const result = vendorFiltered.filter((c: any) => c.instances > 0)

            configStatusFiltered = result
          } else if (
            filters.configurationStatus.length === 1 &&
            filters.configurationStatus.includes('Not configured Instances')
          ) {
            const result = vendorFiltered.filter((c: any) => !c.instances)

            configStatusFiltered = result
          } else if (!filters.configurationStatus.length) {
            configStatusFiltered = vendorFiltered
          }
        }

        if (sortBy === IntegrationsSort.AZ) {
          configStatusFiltered.sort((a: any, b: any) => (a.display_name > b.display_name ? 1 : -1))
        } else if (sortBy === IntegrationsSort.ZA) {
          configStatusFiltered.sort((a: any, b: any) => (a.display_name > b.display_name ? -1 : 1))
        } else if (sortBy === IntegrationsSort.ConfiguredFirst) {
          const withInstances = configStatusFiltered
            .filter((c) => c.instances > 0)
            .sort((a: any, b: any) => a.display_name.localeCompare(b.display_name))
          const withoutInstances = configStatusFiltered
            .filter((c) => !c.instances)
            .sort((a: any, b: any) => a.display_name.localeCompare(b.display_name))
          configStatusFiltered = [...withInstances, ...withoutInstances]
        } else if (sortBy === IntegrationsSort.NonconfiguredFirst) {
          const withInstances = configStatusFiltered
            .filter((c) => c.instances > 0)
            .sort((a: any, b: any) => a.display_name.localeCompare(b.display_name))
          const withoutInstances = configStatusFiltered
            .filter((c) => !c.instances)
            .sort((a: any, b: any) => a.display_name.localeCompare(b.display_name))
          configStatusFiltered = [...withoutInstances, ...withInstances]
        }

        setSupportedIntegrations(configStatusFiltered)
      }
      if (tabValue === 1) {
        const supported = [...supportedIntegrations, ...supportedIntegrationsNew]

        const result = instances

        const configuredWithVendor = result.map((c: any) => {
          const found = supported.find((n: any) => n.name === c.integration_name)
          return {
            ...c,
            vendor: found?.vendor || null
          }
        })

        const vendorFiltered =
          filters.vendor.length > 0
            ? configuredWithVendor.filter((c: any) => (filters.vendor as any).includes(c.vendor.display_name))
            : configuredWithVendor

        const productFiltered =
          filters.product.length > 0
            ? vendorFiltered.filter((c: any) =>
                (filters.product as any).replaceAll(' ', '_').includes(c.integration_name)
              )
            : [...vendorFiltered]

        if (sortBy === IntegrationsSort.AZ) {
          productFiltered.sort((a: any, b: any) => (a.name > b.name ? 1 : -1))
        } else if (sortBy === IntegrationsSort.ZA) {
          productFiltered.sort((a: any, b: any) => (a.name > b.name ? -1 : 1))
        } else if (sortBy === IntegrationsSort.ConfiguredFirst) {
          productFiltered.sort((a: any, b: any) => (a.instances > b.instances ? -1 : 1))
        } else if (sortBy === IntegrationsSort.NonconfiguredFirst) {
          productFiltered.sort((a: any, b: any) => (a.instances > b.instances ? 1 : -1))
        }

        setConfiguredIntegrations(productFiltered)
      }
    }
  }

  const debouncedHandleSearch = useMemo(() => debounce(handleSearch, 300), [filters, tabValue, sortBy])

  const handleChangeSort = async (event: SelectChangeEvent<typeof sortBy>) => {
    setLoading(true)
    const { value } = event.target
    setSortBy(value as IntegrationsSort)

    const categories = filters.category.filter((c) => c !== '')
    const sortByFormatted =
      value === IntegrationsSort.NewestFirst || value === IntegrationsSort.LatestCreated
        ? 'DESC'
        : value === IntegrationsSort.OldestFirst || value === IntegrationsSort.OldestCreated
        ? 'ASC'
        : ''

    let url = '/api/v3/integrations/supported?loadStatus=true'
    if (tabValue === 1) {
      url = '/api/v3/integrations/configured?loadStatus=true'
    }
    if (sortByFormatted) {
      url = `${url}&sort=${sortByFormatted}`
    }
    if (searchValue) {
      url = `${url}&search=${searchValue}`
    }
    if (categories.length > 0) {
      url = `${url}&categories=[${categories.map((n) => `"${n}"`)}]`
    }

    try {
      const res = await axios.get(url)

      if (res.status === 200) {
        const { integrations, instances } = res.data

        if (tabValue === 0) {
          const result = [...integrations]

          const vendorFiltered =
            filters.vendor.length > 0
              ? result.filter((c: any) => (filters.vendor as any).includes(c.vendor.display_name))
              : result

          let configStatusFiltered: any[] = [...vendorFiltered]

          if (filters.configurationStatus.length > 0) {
            if (filters.configurationStatus.length === 2) {
              configStatusFiltered = vendorFiltered
            } else if (
              filters.configurationStatus.length === 1 &&
              filters.configurationStatus.includes('Configured Instances')
            ) {
              const result = vendorFiltered.filter((c: any) => c.instances > 0)

              configStatusFiltered = result
            } else if (
              filters.configurationStatus.length === 1 &&
              filters.configurationStatus.includes('Not configured Instances')
            ) {
              const result = vendorFiltered.filter((c: any) => !c.instances)

              configStatusFiltered = result
            } else if (!filters.configurationStatus.length) {
              configStatusFiltered = vendorFiltered
            }
          }

          if (value === IntegrationsSort.AZ) {
            configStatusFiltered.sort((a: any, b: any) => (a.display_name > b.display_name ? 1 : -1))
          } else if (value === IntegrationsSort.ZA) {
            configStatusFiltered.sort((a: any, b: any) => (a.display_name > b.display_name ? -1 : 1))
          } else if (value === IntegrationsSort.ConfiguredFirst) {
            const withInstances = configStatusFiltered
              .filter((c) => c.instances > 0)
              .sort((a: any, b: any) => a.display_name.localeCompare(b.display_name))
            const withoutInstances = configStatusFiltered
              .filter((c) => !c.instances)
              .sort((a: any, b: any) => a.display_name.localeCompare(b.display_name))
            configStatusFiltered = [...withInstances, ...withoutInstances]
          } else if (value === IntegrationsSort.NonconfiguredFirst) {
            const withInstances = configStatusFiltered
              .filter((c) => c.instances > 0)
              .sort((a: any, b: any) => a.display_name.localeCompare(b.display_name))
            const withoutInstances = configStatusFiltered
              .filter((c) => !c.instances)
              .sort((a: any, b: any) => a.display_name.localeCompare(b.display_name))
            configStatusFiltered = [...withoutInstances, ...withInstances]
          }

          setSupportedIntegrations(configStatusFiltered)
        }

        if (tabValue === 1) {
          const result = [...instances]

          const configuredWithVendor = result.map((c: any) => {
            const found = [...supportedIntegrations, ...supportedIntegrationsNew].find(
              (n: any) => n.name === c.integration_name
            )
            return {
              ...c,
              vendor: found?.vendor || null
            }
          })

          const vendorFiltered =
            filters.vendor.length > 0
              ? configuredWithVendor.filter((c: any) => (filters.vendor as any).includes(c.vendor.display_name))
              : configuredWithVendor

          const productFiltered =
            filters.product.length > 0
              ? vendorFiltered.filter((c: any) => (filters.product as any).includes(c.integration_name))
              : [...vendorFiltered]

          if (value === IntegrationsSort.AZ) {
            // productFiltered.sort((a: any, b: any) => (a.name > b.name ? 1 : -1))
            productFiltered.sort((a: any, b: any) => {
              const nameA = a.name || a.integration_name || ''
              const nameB = b.name || b.integration_name || ''

              const displayA = nameA.replace(/_/g, ' ').toLowerCase()
              const displayB = nameB.replace(/_/g, ' ').toLowerCase()

              return displayA.localeCompare(displayB)
            })
          } else if (value === IntegrationsSort.ZA) {
            // productFiltered.sort((a: any, b: any) => (a.name > b.name ? -1 : 1))
            productFiltered.sort((a: any, b: any) => {
              const nameA = a.name || a.integration_name || ''
              const nameB = b.name || b.integration_name || ''

              const displayA = nameA.replace(/_/g, ' ').toLowerCase()
              const displayB = nameB.replace(/_/g, ' ').toLowerCase()

              return displayB.localeCompare(displayA)
            })
          }

          setConfiguredIntegrations(productFiltered)
        }
      }
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const handleChangeCategory = async (event: SelectChangeEvent<typeof filters.category>) => {
    setLoading(true)
    const {
      target: { value }
    } = event

    const temp = { ...filters } as any
    temp.category = value
    setFilters(temp)

    const filterable = (value as any).filter((c: any) => c !== '')
    const sortByFormatted =
      tabValue === 0
        ? sortBy === IntegrationsSort.NewestFirst
          ? 'DESC'
          : sortBy === IntegrationsSort.OldestFirst
          ? 'ASC'
          : ''
        : sortBy === IntegrationsSort.LatestCreated
        ? 'DESC'
        : sortBy === IntegrationsSort.OldestCreated
        ? 'ASC'
        : ''

    try {
      const supportedBaseUrl = '/api/v3/integrations/supported'
      const configuredBaseUrl = '/api/v3/integrations/configured'

      const url = filterable.length
        ? `?search=${searchValue}&sort=${sortByFormatted}&categories=[${filterable.map((n: any) => `"${n}"`)}]`
        : `?search=${searchValue}&sort=${sortByFormatted}`

      const res = await axios.get(`${supportedBaseUrl}${url}`)
      const configuredRes = await axios.get(`${configuredBaseUrl}${url}`)

      // if (!filterable.length) {
      //   if (res.status === 200) {
      //     if (tabValue === 0) {
      //       const res = await axios.get(`${supportedBaseUrl}?search=${searchValue}&sort=${sortByFormatted}`)
      //       setSupportedIntegrations(res.data.integrations)
      //     } else {
      //       const res = await axios.get(`${configuredBaseUrl}?search=${searchValue}&sort=${sortByFormatted}`)
      //       setConfiguredIntegrations(res.data.instances)
      //     }
      //   }
      //
      //   return
      // }

      if (res.status === 200) {
        if (tabValue === 0) {
          const vendorFiltered =
            filters.vendor.length > 0
              ? res.data.integrations.filter((c: any) => (filters.vendor as any).includes(c.vendor.display_name))
              : res.data.integrations

          let configStatusFiltered: any[] = [...vendorFiltered]

          if (filters.configurationStatus.length > 0) {
            if (filters.configurationStatus.length === 2) {
              configStatusFiltered = vendorFiltered
            } else if (
              filters.configurationStatus.length === 1 &&
              filters.configurationStatus.includes('Configured Instances')
            ) {
              const result = vendorFiltered.filter((c: any) => c.instances > 0)

              configStatusFiltered = result
            } else if (
              filters.configurationStatus.length === 1 &&
              filters.configurationStatus.includes('Not configured Instances')
            ) {
              const result = vendorFiltered.filter((c: any) => !c.instances)

              configStatusFiltered = result
            } else if (!filters.configurationStatus.length) {
              configStatusFiltered = vendorFiltered
            }

            // setSupportedIntegrations(configStatusFiltered)
            // return
          }

          if (sortBy === IntegrationsSort.AZ) {
            configStatusFiltered.sort((a: any, b: any) => (a.display_name > b.display_name ? 1 : -1))
          } else if (sortBy === IntegrationsSort.ZA) {
            configStatusFiltered.sort((a: any, b: any) => (a.display_name > b.display_name ? -1 : 1))
          } else if (sortBy === IntegrationsSort.ConfiguredFirst) {
            const withInstances = configStatusFiltered
              .filter((c) => c.instances > 0)
              .sort((a: any, b: any) => a.display_name.localeCompare(b.display_name))
            const withoutInstances = configStatusFiltered
              .filter((c) => !c.instances)
              .sort((a: any, b: any) => a.display_name.localeCompare(b.display_name))
            configStatusFiltered = [...withInstances, ...withoutInstances]
          } else if (sortBy === IntegrationsSort.NonconfiguredFirst) {
            const withInstances = configStatusFiltered
              .filter((c) => c.instances > 0)
              .sort((a: any, b: any) => a.display_name.localeCompare(b.display_name))
            const withoutInstances = configStatusFiltered
              .filter((c) => !c.instances)
              .sort((a: any, b: any) => a.display_name.localeCompare(b.display_name))
            configStatusFiltered = [...withoutInstances, ...withInstances]
          }

          setSupportedIntegrations(configStatusFiltered)
        } else {
          const result = configuredRes.data.instances
          const configuredWithVendor = result.map((c: any) => {
            const found = res.data.integrations.find((n: any) => n.name === c.integration_name)
            return {
              ...c,
              vendor: found?.vendor || null
            }
          })

          const vendorFiltered =
            filters.vendor.length > 0
              ? configuredWithVendor.filter((c: any) => (filters.vendor as any).includes(c.vendor.display_name))
              : configuredWithVendor

          const productFiltered =
            filters.product.length > 0
              ? vendorFiltered.filter((c: any) => (filters.product as any).includes(c.integration_name))
              : [...vendorFiltered]

          if (sortBy === IntegrationsSort.AZ) {
            productFiltered.sort((a: any, b: any) => (a.display_name > b.display_name ? 1 : -1))
          } else if (sortBy === IntegrationsSort.ZA) {
            productFiltered.sort((a: any, b: any) => (a.display_name > b.display_name ? -1 : 1))
          } else if (sortBy === IntegrationsSort.ConfiguredFirst) {
            productFiltered.sort((a: any, b: any) => (a.instances > b.instances ? -1 : 1))
          } else if (sortBy === IntegrationsSort.NonconfiguredFirst) {
            productFiltered.sort((a: any, b: any) => (a.instances > b.instances ? 1 : -1))
          }

          setConfiguredIntegrations(productFiltered)
        }
      }
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  const handleChangeVendor = async (event: SelectChangeEvent<typeof selectedVendors>) => {
    setLoading(true)
    const {
      target: { value }
    } = event

    const temp = { ...filters } as any
    temp.vendor = value
    setFilters(temp)
    setSelectedVendors(value as any)

    const categories = filters.category.filter((n) => n !== '')
    const filterable = (value as any).filter((c: any) => c !== '')
    const sortByFormatted =
      tabValue === 0
        ? sortBy === IntegrationsSort.NewestFirst
          ? 'DESC'
          : sortBy === IntegrationsSort.OldestFirst
          ? 'ASC'
          : ''
        : sortBy === IntegrationsSort.LatestCreated
        ? 'DESC'
        : sortBy === IntegrationsSort.OldestCreated
        ? 'ASC'
        : ''

    try {
      let baseUrl = '/api/v3/integrations/supported'
      if (tabValue === 1) {
        baseUrl = '/api/v3/integrations/configured'
      }

      if (!filterable.length) {
        const res = await axios.get(
          `${baseUrl}?search=${searchValue}&sort=${sortByFormatted}&categories=[${categories.map((n) => `"${n}"`)}]`
        )

        if (res.status === 200) {
          if (tabValue === 0) {
            setSupportedIntegrations(res.data.integrations)
          } else {
            setConfiguredIntegrations(res.data.instances)
          }
        }

        return
      }

      const url = `${baseUrl}?search=${searchValue}&categories=[${filters.category.map(
        (c) => `"${c}"`
      )}]&sort=${sortByFormatted}`

      const res = await axios.get(url)

      if (res.status === 200) {
        if (tabValue === 0) {
          const data = res.data.integrations.filter((c: any) => (value as any).includes(c.vendor.display_name))
          let configStatusFiltered: any[] = [...data]
          if (value.length === 2 && value.includes('Not configured Instances')) {
            const result = data.filter((c: any) => !c.instances)

            configStatusFiltered = result
          } else if (value.length === 2 && value.includes('Configured instances')) {
            const result = data.filter((c: any) => c.instances > 0)

            configStatusFiltered = result
          }

          if (sortBy === IntegrationsSort.AZ) {
            configStatusFiltered.sort((a: any, b: any) => (a.display_name > b.display_name ? 1 : -1))
          } else if (sortBy === IntegrationsSort.ZA) {
            configStatusFiltered.sort((a: any, b: any) => (a.display_name > b.display_name ? -1 : 1))
          } else if (sortBy === IntegrationsSort.ConfiguredFirst) {
            const withInstances = configStatusFiltered
              .filter((c) => c.instances > 0)
              .sort((a: any, b: any) => a.display_name.localeCompare(b.display_name))
            const withoutInstances = configStatusFiltered
              .filter((c) => !c.instances)
              .sort((a: any, b: any) => a.display_name.localeCompare(b.display_name))
            configStatusFiltered = [...withInstances, ...withoutInstances]
          } else if (sortBy === IntegrationsSort.NonconfiguredFirst) {
            const withInstances = configStatusFiltered
              .filter((c) => c.instances > 0)
              .sort((a: any, b: any) => a.display_name.localeCompare(b.display_name))
            const withoutInstances = configStatusFiltered
              .filter((c) => !c.instances)
              .sort((a: any, b: any) => a.display_name.localeCompare(b.display_name))
            configStatusFiltered = [...withoutInstances, ...withInstances]
          }

          setSupportedIntegrations(configStatusFiltered)
        } else {
          const configured = res.data.instances
          const supported = [...supportedIntegrations, ...supportedIntegrationsNew]
          const configuredWithVendor = configured.map((c: any) => {
            const found = supported.find((n: any) => n.name === c.integration_name)
            return {
              ...c,
              vendor: found?.vendor || null
            }
          })
          const result = configuredWithVendor.filter((c: any) => (value as any).includes(c.vendor.display_name))

          if (sortBy === IntegrationsSort.AZ) {
            result.sort((a: any, b: any) => (a.name > b.name ? 1 : -1))
          } else if (sortBy === IntegrationsSort.ZA) {
            result.sort((a: any, b: any) => (a.name > b.name ? -1 : 1))
          } else if (sortBy === IntegrationsSort.ConfiguredFirst) {
            result.sort((a: any, b: any) => (a.instances > b.instances ? -1 : 1))
          } else if (sortBy === IntegrationsSort.NonconfiguredFirst) {
            result.sort((a: any, b: any) => (a.instances > b.instances ? 1 : -1))
          }

          setConfiguredIntegrations(result)
        }
      }
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  const handleChangeProduct = async (event: SelectChangeEvent<typeof selectedVendors>) => {
    setLoading(true)
    const {
      target: { value }
    } = event

    const temp = { ...filters } as any
    temp.product = value
    setFilters(temp)
    // setSelectedVendors(value as any)

    // const categories = filters.category.filter((n) => n !== '')
    // const filterable = (value as any).filter((c: any) => c !== '')
    const sortByFormatted =
      tabValue === 0
        ? sortBy === IntegrationsSort.NewestFirst
          ? 'DESC'
          : sortBy === IntegrationsSort.OldestFirst
          ? 'ASC'
          : ''
        : sortBy === IntegrationsSort.LatestCreated
        ? 'DESC'
        : sortBy === IntegrationsSort.OldestCreated
        ? 'ASC'
        : ''

    try {
      const baseUrl = '/api/v3/integrations/configured'

      // if (!filterable.length) {
      //   const res = await axios.get(
      //     `${baseUrl}?search=${searchValue}&sort=${sortByFormatted}&categories=[${categories.map((n) => `"${n}"`)}]`
      //   )
      //
      //   if (res.status === 200) {
      //     setConfiguredIntegrations(res.data.instances)
      //   }
      //
      //   return
      // }

      const url = `${baseUrl}?search=${searchValue}&categories=[${filters.category.map(
        (c) => `"${c}"`
      )}]&sort=${sortByFormatted}`

      const res = await axios.get(url)

      if (res.status === 200) {
        const valueFormatted = (value as any[]).map((c) => c.replaceAll(' ', '_'))
        const configured = res.data.instances

        const supported = [...supportedIntegrations, ...supportedIntegrationsNew]

        const configuredWithVendor = configured.map((c: any) => {
          const found = supported.find((n: any) => n.name === c.integration_name)
          return {
            ...c,
            vendor: found?.vendor || null
          }
        })

        let result = [...configuredWithVendor]
        if (filters.vendor.length > 0) {
          result = configuredWithVendor.filter((c: any) => (filters.vendor as any).includes(c.vendor.display_name))
        }

        const resultWithProduct =
          valueFormatted.length > 0 ? result.filter((n: any) => valueFormatted.includes(n.integration_name)) : result

        if (sortBy === IntegrationsSort.AZ) {
          resultWithProduct.sort((a: any, b: any) => (a.name > b.name ? 1 : -1))
        } else if (sortBy === IntegrationsSort.ZA) {
          resultWithProduct.sort((a: any, b: any) => (a.name > b.name ? -1 : 1))
        } else if (sortBy === IntegrationsSort.ConfiguredFirst) {
          resultWithProduct.sort((a: any, b: any) => (a.instances > b.instances ? -1 : 1))
        } else if (sortBy === IntegrationsSort.NonconfiguredFirst) {
          resultWithProduct.sort((a: any, b: any) => (a.instances > b.instances ? 1 : -1))
        }

        setConfiguredIntegrations(resultWithProduct)
      }
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  const handleChangeConfigurationStatus = async (event: SelectChangeEvent<typeof instanceFilter>) => {
    setLoading(true)
    const {
      target: { value }
    } = event

    const temp = { ...filters } as any
    temp.configurationStatus = value
    setFilters(temp)

    const sortByFormatted =
      tabValue === 0
        ? sortBy === IntegrationsSort.NewestFirst
          ? 'DESC'
          : sortBy === IntegrationsSort.OldestFirst
          ? 'ASC'
          : ''
        : sortBy === IntegrationsSort.LatestCreated
        ? 'DESC'
        : sortBy === IntegrationsSort.OldestCreated
        ? 'ASC'
        : ''

    const categories = filters.category.filter((n) => n !== '')

    let baseUrl = '/api/v3/integrations/supported'
    if (tabValue === 1) {
      baseUrl = '/api/v3/integrations/configured'
    }
    const url = `${baseUrl}?search=${searchValue}&sort=${sortByFormatted}&categories=[${categories.map(
      (n) => `"${n}"`
    )}]`

    try {
      const res = await axios.get(url)

      if (res.status === 200) {
        const { integrations } = res.data

        let configStatusFiltered: any[] = []
        if (value.length === 2) {
          configStatusFiltered = integrations
        } else if (value.length === 1 && value.includes('Configured Instances')) {
          const result = integrations.filter((c: any) => c.instances > 0)

          configStatusFiltered = result
        } else if (value.length === 1 && value.includes('Not configured Instances')) {
          const result = integrations.filter((c: any) => !c.instances)

          configStatusFiltered = result
        } else if (!value.length) {
          configStatusFiltered = integrations
        }

        if (filters.vendor.length > 0) {
          const vendorFiltered = configStatusFiltered.filter((c: any) =>
            (filters.vendor as any).includes(c.vendor.display_name)
          )
          configStatusFiltered = vendorFiltered
        }

        if (sortBy === IntegrationsSort.AZ) {
          configStatusFiltered.sort((a: any, b: any) => (a.display_name > b.display_name ? 1 : -1))
        } else if (sortBy === IntegrationsSort.ZA) {
          configStatusFiltered.sort((a: any, b: any) => (a.display_name > b.display_name ? -1 : 1))
        } else if (sortBy === IntegrationsSort.ConfiguredFirst) {
          const withInstances = configStatusFiltered
            .filter((c) => c.instances > 0)
            .sort((a: any, b: any) => a.display_name.localeCompare(b.display_name))
          const withoutInstances = configStatusFiltered
            .filter((c) => !c.instances)
            .sort((a: any, b: any) => a.display_name.localeCompare(b.display_name))
          configStatusFiltered = [...withInstances, ...withoutInstances]
        } else if (sortBy === IntegrationsSort.NonconfiguredFirst) {
          const withInstances = configStatusFiltered
            .filter((c) => c.instances > 0)
            .sort((a: any, b: any) => a.display_name.localeCompare(b.display_name))
          const withoutInstances = configStatusFiltered
            .filter((c) => !c.instances)
            .sort((a: any, b: any) => a.display_name.localeCompare(b.display_name))
          configStatusFiltered = [...withoutInstances, ...withInstances]
        }

        setSupportedIntegrations(configStatusFiltered)
      }
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const handleOpenDetails = () => {
    setDetailsOpen(!detailsOpen)
  }

  const handleTabChange = async (event: SyntheticEvent, newValue: number) => {
    setLoading(true)
    /* Reset Filters */
    setSearchValue('')
    setFilters({
      category: [],
      vendor: [],
      configurationStatus: [],
      product: []
    })
    setInstanceFilter([])
    setSelectedIntegration(null)
    setSelectedInstance(null)
    setSortBy(IntegrationsSort.AZ)

    const sortByFormatted =
      tabValue === 0
        ? sortBy === IntegrationsSort.NewestFirst
          ? 'DESC'
          : sortBy === IntegrationsSort.OldestFirst
          ? 'ASC'
          : ''
        : sortBy === IntegrationsSort.LatestCreated
        ? 'DESC'
        : sortBy === IntegrationsSort.OldestCreated
        ? 'ASC'
        : ''

    if (newValue === 0) {
      setDetailsOpen(false)
      setSelectedInstance(null)
    }
    setTabValue(newValue)

    const supportedUrl = `/api/v3/integrations/supported?search=${searchValue}&sort=${sortByFormatted}`
    const configuredUrl = `/api/v3/integrations/configured?loadStatus=true&search=${searchValue}&sort=${sortByFormatted}`

    try {
      const res = await axios.get(supportedUrl)
      const configuredRes = await axios.get(configuredUrl)

      if (res.status === 200) {
        setSupportedIntegrations(
          res.data.integrations.sort((a: any, b: any) => (a.display_name > b.display_name ? 1 : -1))
        )

        const configured = configuredRes.data.instances
        const configuredWithVendor = configured.map((c: any) => {
          const found = res.data.integrations.find((n: any) => n.name === c.integration_name)
          return {
            ...c,
            vendor: found?.vendor || null
          }
        })
        if (sortBy === IntegrationsSort.AZ) {
          configuredWithVendor.sort((a: any, b: any) => {
            const nameA = a.name || a.integration_name || ''
            const nameB = b.name || b.integration_name || ''

            const displayA = nameA.replace(/_/g, ' ').toLowerCase()
            const displayB = nameB.replace(/_/g, ' ').toLowerCase()

            return displayA.localeCompare(displayB)
          })
        } else if (sortBy === IntegrationsSort.ZA) {
          configuredWithVendor.sort((a: any, b: any) => {
            const nameA = a.name || a.integration_name || ''
            const nameB = b.name || b.integration_name || ''

            const displayA = nameA.replace(/_/g, ' ').toLowerCase()
            const displayB = nameB.replace(/_/g, ' ').toLowerCase()

            return displayB.localeCompare(displayA)
          })
        }
        setConfiguredIntegrations(configuredWithVendor)
      }
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  const deleteInstanceCallback = async () => {
    try {
      setDrawerOpen(false)
      const configured = await fetchConfigured()
      setConfiguredIntegrations(configured)
      const state = location.state
      if (state && (state as any).instanceId) {
        const instanceId = (state as any).instanceId
        if (instanceId) {
          const instance = configured.find((n) => n.id === instanceId)
          if (instance) {
            setTabValue(1)
            setSelectedInstance(instance)
            setDetailsOpen(true)
            setTimeout(() => {
              navigate(location.pathname, {})
            }, 0)
          }
        }
      } else {
        setSelectedInstance(null)
        setSelectedIntegration(null)
      }
    } catch (err) {
      console.error(err)
    }
  }

  const handleDeleteInstance = () => {
    if (selectedInstance) {
      dispatch(
        openModal({
          type: ModalType.deleteIntegrationInstance,
          props: {
            isAnalyzer: !!instances.find((item) => item.integration_name === selectedInstance.integration_name),
            integrationInstance: selectedInstance,
            callback: deleteInstanceCallback,
            closeCallback: () => {
              setLoading(false)
              setDetailsOpen(false)
              setDrawerOpen(false)
            }
          }
        })
      )
    }
  }

  const findAffectedCpis = async (cpis: CPILibraryItem[], instanceId: string) => {
    try {
      const affectedCpis: CPILibraryItem[] = []

      cpis.forEach((n) => {
        if (n.configuration) {
          const hasIntegration = n.configuration.integrations.find(
            (c: CPILibraryConfigurationIntegration) => c.id === instanceId
          )
          if (hasIntegration) {
            affectedCpis.push(n)
          }
        }
      })

      return affectedCpis
    } catch (e) {
      console.error(e)
    } finally {
      setAffectedCpisLoading(false)
    }
  }

  const getAllAffectedCpis = async () => {
    try {
      const cpis = await fetchCpis()
      const filteredCpis = cpis.filter((n: CPILibraryItem) => !!n.configuration)

      const promises: Promise<CPILibraryItem[]>[] = configuredIntegrations.map(async (n) => {
        const resp = await findAffectedCpis(filteredCpis, n.id)
        if (resp) {
          return resp
        }
        return []
      })

      const resolved: any = await Promise.all(promises)
      setAffectedCpis(resolved)
    } catch (err) {
      console.error(err)
    } finally {
      setAffectedCpisLoading(false)
    }
  }

  useEffect(() => {
    if (configuredIntegrations.length > 0 && affectedCpisLoading) {
      getAllAffectedCpis().catch((e) => e)
    }
  }, [configuredIntegrations, affectedCpisLoading])

  const renderAffectedCpis = (): JSX.Element | null => {
    if (!affectedCpis || (!selectedInstance && !selectedIntegration) || !cpis.length) return null

    if (selectedInstance) {
      const withConfig: CPILibraryItem[] = cpis.filter((n: CPILibraryItem) => !!n.configuration)
      const theseCpis: CPILibraryItem[] = []

      withConfig.forEach((n) => {
        if (n.configuration) {
          const hasIntegration = n.configuration.integrations.find(
            (c: CPILibraryConfigurationIntegration) => c.id === selectedInstance.id
          )
          if (hasIntegration) {
            theseCpis.push(n)
          }
        }
      })
      const findItem = instances.find((item) => item.integration_name === selectedInstance.integration_name)

      if (!theseCpis.length && !findItem) return null
      theseCpis.sort((a, b) => (a.display_name > b.display_name ? 1 : -1))
      return (
        <CpisListWrapper className="cpis-list-wrapper">
          <Box className="cpis-list-header">
            <Typography className="cpis-list-title">CPIs ({theseCpis.length})</Typography>
            <Typography className="cpis-list-description">CPI elements configured with this integration:</Typography>
          </Box>

          {findItem && (
            <Box className="analyzer-block">
              <AssetAnalyzer />
              <Typography className="title">Asset Coverage Analyzer</Typography>
            </Box>
          )}

          <Box className="cpis-list-inner-wrapper">
            {theseCpis.map((cpi, i) => (
              <Box
                className="cpis-list-item"
                key={i}
                onClick={() => window.open(`/library/cpis/${cpi.display_name.toLowerCase()}/trends`, '_blank')}
              >
                <Box className="cpis-list-chip">{cpi.display_name}</Box>
                <Typography className="cpis-list-title">{cpi.title}</Typography>
                <IconButton>
                  <ChevronRight />
                </IconButton>
              </Box>
            ))}
          </Box>
        </CpisListWrapper>
      )
    }

    if (!selectedIntegration) return null

    const supportedCpis = selectedIntegration.supported_cpis || []
    const theseCpis: CPILibraryItem[] = cpis
      .filter((c) => supportedCpis.find((n) => n.name === c.name))
      .sort((a, b) => (a.display_name > b.display_name ? 1 : -1))

    if (!theseCpis.length) return null
    return (
      <CpisListWrapper className="cpis-list-wrapper">
        <Box className="cpis-list-header">
          <Typography className="cpis-list-title">CPIs ({theseCpis.length})</Typography>
          <Typography className="cpis-list-description">CPI elements supported with this integration:</Typography>
        </Box>

        <Box className="cpis-list-inner-wrapper">
          {theseCpis.map((cpi, i) => (
            <Box
              className="cpis-list-item"
              key={i}
              onClick={() => window.open(`/library/cpis/${cpi.display_name.toLowerCase()}/trends`, '_blank')}
            >
              <Box className="cpis-list-chip">{cpi.display_name}</Box>
              <Typography className="cpis-list-title">{cpi.title}</Typography>
              <IconButton>
                <ChevronRight />
              </IconButton>
            </Box>
          ))}
        </Box>
      </CpisListWrapper>
    )
  }

  const noEditPermission = useMemo(() => hasAnyRole([Role.Viewer, Role.Demo], authUser), [authUser])

  const handleReset = (e: ChangeEvent<any>, type: string) => {
    const temp = { ...filters } as any
    temp[type] = type === 'sortBy' ? 'Newest to Oldest' : []
    setFilters(temp)

    if (type === 'category') {
      handleChangeCategory({ target: { value: [] } } as any)
    }
    if (type === 'vendor') {
      handleChangeVendor({ target: { value: [] } } as any)
    }
  }

  const handleOpenDrawer = (integration: ManifestIntegration | IntegrationInstance) => {
    setDrawerOpen(true)

    if (instanceOfManifestIntegration(integration)) {
      setSelectedIntegration(integration)
      setIntegrationType('add')
    } else {
      setSelectedInstance(integration)
      setIntegrationType('edit')
    }
  }

  const handleViewInstances = (integration: ManifestIntegration | null) => {
    handleOpenDetails()
    setTabValue(1)
    const thisVendor: any = [(integration as any).vendor.display_name]
    setSelectedVendors(thisVendor)
    setDrawerOpen(false)
    setSelectedIntegration(null)
    setSelectedInstance(null)
    if (!integration) return
    const test = configuredIntegrations.filter((c: any) => integration.name === c.integration_name)
    setConfiguredIntegrations(test)
    const products: string[] = []
    test.forEach((c) => {
      const res = c.integration_name.replaceAll('_', ' ')
      if (!products.includes(res)) {
        products.push(res)
      }
    })
    setFilters((pre) => ({
      ...pre,
      vendor: thisVendor,
      product: products
    }))
  }

  const handleAddInstance = (integrationName: string) => navigate(`/settings/integrations/data-sources/${integrationName}/add`)

  const handleViewInstance = (integration: ManifestIntegration) => {
    handleOpenDetails()
    setTabValue(1)
    const thisVendor: any = [(integration as any).vendor.display_name]
    setSelectedVendors(thisVendor)
    setDrawerOpen(false)
    setSelectedIntegration(null)
    setSelectedInstance(null)
    const test = configuredIntegrations.filter((c: any) => integration.name === c.integration_name)
    setConfiguredIntegrations(test)
    const products: string[] = []
    test.forEach((c) => {
      const res = c.integration_name.replaceAll('_', ' ')
      if (!products.includes(res)) {
        products.push(res)
      }
    })
    setFilters((pre) => ({
      ...pre,
      vendor: thisVendor,
      product: products
    }))
  }

  const handleResetAll = async () => {
    setSearchValue('')
    setFilters({
      /* ONYXIA: 5780 */
      category: [],
      // category: categoryOptions,
      vendor: [],
      configurationStatus: [],
      product: []
    })
    setInstanceFilter([])
    await fetchInstances()
  }

  return (
    <AppContainer className="app-container">
      <MainContent className="main-content">
        <Header className="header">
          <Typography variant="h1" className="title" fontWeight={500} mb={4}>
            {isTicketingSystemsEnabled ? DATA_SOURCES_PAGE_TITLE : INTEGRATIONS_PAGE_TITLE}
          </Typography>

          <StyledTabs value={tabValue} onChange={handleTabChange}>
            <StyledTab
              label={`${DIRECTORY_TAB_LABEL} (${supportedIntegrationsAll.length + filteredNewIntegrations.length})`}
              {...a11yProps(0)}
            />
            <StyledTab label={`Instances (${configuredIntegrations.length})`} {...a11yProps(1)} />
          </StyledTabs>
        </Header>

        <TabPanel value={tabValue} index={0} exists={supportedIntegrations.length}>
          <ControlsSection className="controls-section">
            <FiltersContainer className="filters-container">
              <IntegrationsSearch
                resetKey={key}
                searchValue={searchValue}
                setSearchValue={setSearchValue}
                onInputChange={debouncedHandleSearch}
                placeholder="Search"
              />

              <IntegrationsSelectCustom
                label="Product Type"
                options={categoryOptions}
                placeholder="Product Type"
                value={filters.category}
                multiple
                handleReset={(e: ChangeEvent<any>) => handleReset(e, 'category')}
                field="category"
                handleChange={handleChangeCategory}
                maxHeight="296px"
              />

              <IntegrationsSelectCustom
                label="Vendor"
                options={vendorOptions}
                placeholder="vendor"
                value={filters.vendor}
                multiple
                handleChange={handleChangeVendor}
                handleReset={(e: ChangeEvent<any>) => handleReset(e, 'vendor')}
                field="vendor"
                maxHeight="296px"
              />

              <IntegrationsSelectField
                label="Configuration Status"
                options={integrationsInstancesOptions}
                placeholder="configuration status"
                value={filters.configurationStatus}
                handleChange={handleChangeConfigurationStatus}
                multiple
              />

              <ResetButton variant="outlined" onClick={handleResetAll}>
                Reset all
              </ResetButton>
            </FiltersContainer>

            <Box className="sort-wrapper">
              <Typography className="number-of-items" variant="body2">
                Number of items: <span>{supportedIntegrationsAll.length + filteredNewIntegrations.length}</span>
              </Typography>

              <SortButtonSelect
                options={integrationsSortOptionsIntegrations}
                value={sortBy}
                handleChange={handleChangeSort}
              />
            </Box>
          </ControlsSection>

          <ScrollableContent className="scrollable-content">
            {pageLoading || loading ? (
              <IntegrationsGridWrapper>
                <GridContainer>
                  {Array.from(Array(20).keys()).map((c, i) => (
                    <SSMCardSkeletonWrapper sx={{ gap: `${theme.spacing.xSmall}px !important` }} key={i}>
                      <SSMCardSkeleton sx={{ height: '200px !important' }} className="integrations">
                        <Skeleton
                          variant="rectangular"
                          component="div"
                          animation="wave"
                          width="100%"
                          height="100%"
                          className="card-skeleton-inner"
                        />
                      </SSMCardSkeleton>
                    </SSMCardSkeletonWrapper>
                  ))}
                </GridContainer>
              </IntegrationsGridWrapper>
            ) : (
              <>
                {supportedIntegrations.length > 0 ? (
                  <>
                    {filteredNewIntegrations.length > 0 && (
                      <IntegrationsGridWrapper className="integrations-list-wrapper">
                        <Box className="title-wrapper">
                          <Typography variant="h5" className="integrations-category-title" fontWeight={500}>
                            New
                          </Typography>
                        </Box>

                        <GridContainer>
                          {filteredNewIntegrations.map((integration) => (
                            <IntegrationCard
                              configuredIntegrations={configuredIntegrations}
                              handleOpenDrawer={handleOpenDrawer}
                              key={integration.display_name}
                              integration={integration}
                              supportedIntegrations={supportedIntegrations}
                              searchValue={searchValue}
                              handleViewInstance={handleViewInstance}
                            />
                          ))}
                        </GridContainer>
                      </IntegrationsGridWrapper>
                    )}

                    {supportedIntegrationsAll.length > 0 && (
                      <IntegrationsGridWrapper>
                        {filteredNewIntegrations.length > 0 && (
                          <Box className="title-wrapper">
                            <Typography variant="h5" className="integrations-category-title" fontWeight={500}>
                              All Integrations
                            </Typography>
                          </Box>
                        )}

                        <GridContainer>
                          {supportedIntegrationsAll.map((integration) => (
                            <IntegrationCard
                              configuredIntegrations={configuredIntegrations}
                              handleOpenDrawer={handleOpenDrawer}
                              key={integration.display_name}
                              integration={integration}
                              supportedIntegrations={supportedIntegrations}
                              searchValue={searchValue}
                              handleViewInstance={handleViewInstance}
                            />
                          ))}
                        </GridContainer>
                      </IntegrationsGridWrapper>
                    )}
                  </>
                ) : (
                  !pageLoading &&
                  !loading &&
                  (totalIntegrationsCount === 0 ? (
                    <EmptyState
                      title="No Results Found"
                      description="There are no integrations available at the moment. Start by creating a new integration to manage and monitor your environment efficiently."
                      variant="instances"
                    />
                  ) : (
                    <EmptyState
                      title="No Results Found"
                      description="Your filter did not match any issues, try adjusting the settings for better results"
                      variant="instances"
                    />
                  ))
                )}
              </>
            )}
          </ScrollableContent>
        </TabPanel>

        <TabPanel value={tabValue} index={1} exists={configuredIntegrations.length}>
          <ControlsSection className="controls-section">
            <FiltersContainer className="filters-container">
              <IntegrationsSearch
                resetKey={key}
                searchValue={searchValue}
                setSearchValue={setSearchValue}
                onInputChange={debouncedHandleSearch}
                placeholder="Search"
              />

              <IntegrationsSelectCustom
                label="Product Type"
                options={categoryOptions}
                placeholder="Product Type"
                value={filters.category}
                multiple
                handleReset={(e: ChangeEvent<any>) => handleReset(e, 'category')}
                field="category"
                handleChange={handleChangeCategory}
                maxHeight="296px"
              />

              <IntegrationsSelectCustom
                label="Vendor"
                options={vendorOptions}
                placeholder="vendor"
                value={filters.vendor}
                multiple
                handleChange={handleChangeVendor}
                handleReset={(e: ChangeEvent<any>) => handleReset(e, 'vendor')}
                field="vendor"
                maxHeight="296px"
              />

              <IntegrationsSelectCustom
                label="Product"
                options={productOptions}
                placeholder=""
                value={filters.product}
                handleChange={handleChangeProduct}
                multiple
                shouldCapitalize
                handleReset={(e: ChangeEvent<any>) => handleReset(e, 'product')}
                field="product"
                maxHeight="296px"
              />

              <ResetButton variant="outlined" onClick={handleResetAll}>
                Reset all
              </ResetButton>
            </FiltersContainer>

            <Box className="sort-wrapper">
              <Typography className="number-of-items" variant="body2">
                Number of items: <span>{configuredIntegrations.length}</span>
              </Typography>

              <SortButtonSelect
                options={integrationsSortOptionsInstances}
                value={sortBy}
                handleChange={handleChangeSort}
              />
            </Box>
          </ControlsSection>

          <ScrollableContent className="scrollable-content">
            {pageLoading || loading ? (
              <IntegrationsGridWrapper>
                <GridContainer>
                  {Array.from(Array(20).keys()).map((c, i) => (
                    <SSMCardSkeletonWrapper sx={{ gap: `${theme.spacing.xSmall}px !important` }} key={i}>
                      <SSMCardSkeleton sx={{ height: '200px !important' }} className="integrations">
                        <Skeleton
                          variant="rectangular"
                          component="div"
                          animation="wave"
                          width="100%"
                          height="100%"
                          className="card-skeleton-inner"
                        />
                      </SSMCardSkeleton>
                    </SSMCardSkeletonWrapper>
                  ))}
                </GridContainer>
              </IntegrationsGridWrapper>
            ) : (
              <>
                {configuredIntegrations.length > 0 ? (
                  <IntegrationsGridWrapper className="integrations-list-wrapper">
                    <GridContainer>
                      {configuredIntegrations.map((integration) => (
                        <IntegrationCard
                          configuredIntegrations={configuredIntegrations}
                          handleOpenDrawer={handleOpenDrawer}
                          key={integration.id}
                          integration={integration}
                          supportedIntegrations={supportedIntegrations}
                          searchValue={searchValue}
                          handleViewInstance={handleViewInstance}
                        />
                      ))}
                    </GridContainer>
                  </IntegrationsGridWrapper>
                ) : totalInstancesCount === 0 ? (
                  <EmptyState
                    title="No Results Found"
                    description="There are no instances available at the moment. Start by creating a new instance to manage and monitor your environment efficiently."
                    variant="instances"
                  />
                ) : (
                  <EmptyState
                    title="No Results Found"
                    description="Your filter did not match any issues, try adjusting the settings for better results"
                    variant="instances"
                  />
                )}
              </>
            )}
          </ScrollableContent>
        </TabPanel>
      </MainContent>
      <IntegrationInstanceDrawer
        drawerOpen={drawerOpen}
        setDrawerOpen={setDrawerOpen}
        integration={selectedIntegration}
        instance={selectedInstance}
        renderAffectedCpis={renderAffectedCpis}
        instances={configuredIntegrations}
        integrations={[...supportedIntegrations, ...supportedIntegrationsNew]}
        integrationType={integrationType}
        handleDeleteInstance={handleDeleteInstance}
        noEditPermission={noEditPermission}
        handleViewInstances={handleViewInstances}
        handleAddInstance={handleAddInstance}
      />
    </AppContainer>
  )
}

interface TabPanelProps {
  children?: ReactNode
  index: number
  value: number
  exists: number
}

function a11yProps(index: number) {
  return {
    id: `integrations-tab-${index}`,
    'aria-controls': `integrations-tabpanel-${index}`
  }
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`integrations-tabpanel-${index}`}
      aria-labelledby={`integrations-tab-${index}`}
      {...other}
    >
      {value === index && children}
    </Box>
  )
}

export default Integrations
